import React, { useMemo, useState } from "react";
import parse, { domToReact, attributesToProps } from "html-react-parser";
import { helpPage, scripts } from "./help-content.js";
import { convert } from "./converter.js";
import { useApp, TypingInput } from "./common.jsx";
const names = ["Mkhedruli", "Mtavruli", "Asomtavruli", "Nuskhuri"];
const samples = [
  "ქართული ამბავი",
  "ᲥᲐᲠᲗᲣᲚᲘ ᲐᲛᲑᲐᲕᲘ",
  "ႵႠႰႧႳႪႨ ႠႫႡႠႥႨ",
  "ⴕⴀⴐⴇⴓⴊⴈ ⴀⴋⴁⴀⴅⴈ",
];
const ranges = [
  "U+10D0–U+10FF",
  "U+1C90–U+1CBF",
  "U+10A0–U+10C5",
  "U+2D00–U+2D2F",
];
const forms = (c) => {
  const n = c.codePointAt(0);
  return [
    c,
    String.fromCodePoint(n + 0xbc0),
    String.fromCodePoint(n - 0x30),
    String.fromCodePoint(n + 0x1c30),
  ];
};
export function Help() {
  const a = useApp();
  const [letter, setLetter] = useState("ა"),
    [words, setWords] = useState("ქართული ამბავი"),
    [input, setInput] = useState("gamarjoba, saqarTvelo!"),
    [voice, setVoice] = useState(0),
    [script, setScript] = useState(0),
    [raw, setRaw] = useState(false),
    [answer, setAnswer] = useState(""),
    [step, setStep] = useState(0),
    [diagnosis, setDiagnosis] = useState(0);
  const markup = useMemo(() => helpPage(a.lang), [a.lang]);
  const doc = useMemo(
    () => new DOMParser().parseFromString(markup, "text/html"),
    [markup],
  );
  const tours = [...doc.querySelectorAll("#tour-content article")],
    diagnoses = [...doc.querySelectorAll("#diagnosis-content article")];
  const options = {
    replace(node) {
      if (node.type !== "tag") return;
      const p = node.attribs || {},
        id = p.id;
      const children = () => domToReact(node.children, options);
      const button = (handler, extra = {}) =>
        React.createElement(
          node.name,
          { ...attributesToProps(p), ...extra, onClick: handler },
          children(),
        );
      if ("data-start-tour" in p) return button(a.startTour);
      if (p["data-embed-add"])
        return button(() => a.addEmbed(p["data-embed-add"]));
      if (p["data-sample-open"])
        return button(() => a.open("studio", p["data-sample-open"]));
      if (p["data-lab-letter"])
        return button(() => setLetter(p["data-lab-letter"]), {
          "aria-pressed": letter === p["data-lab-letter"],
        });
      if ("data-letter-form" in p)
        return (
          <strong data-letter-form={p["data-letter-form"]}>
            {forms(letter)[+p["data-letter-form"]]}
          </strong>
        );
      if ("data-letter-code" in p) {
        const n = forms(letter)[+p["data-letter-code"]].codePointAt(0);
        return (
          <code data-letter-code={p["data-letter-code"]}>
            {"U+" + n.toString(16).toUpperCase()}
          </code>
        );
      }
      if (id === "helpscriptinput")
        return (
          <TypingInput
            id={id}
            value={words}
            onChange={setWords}
            aria-label="Script playground text"
          />
        );
      if ("data-script-output" in p)
        return (
          <p data-script-output={p["data-script-output"]}>
            {[...words]
              .map((c) => {
                const n = c.codePointAt(0);
                return n >= 0x10d0 && n <= 0x10f0
                  ? forms(c)[+p["data-script-output"]]
                  : c;
              })
              .join("")}
          </p>
        );
      if ("data-help-voice" in p)
        return button(() => setVoice(+p["data-help-voice"]), {
          "aria-pressed": voice === +p["data-help-voice"],
        });
      if (id === "voice-specimen")
        return (
          <strong id={id} className={"voice-" + voice}>
            ქართული
          </strong>
        );
      if ("data-help-script" in p)
        return button(() => setScript(+p["data-help-script"]), {
          "aria-selected": script === +p["data-help-script"],
        });
      if (p.class === "script-example")
        return <div className="script-example">{samples[script]}</div>;
      if (id === "script-panel")
        return (
          <div id={id} className="script-panel" role="tabpanel">
            <div className="script-example">{samples[script]}</div>
            <code>{ranges[script]}</code>
            <p>{scripts[script][a.lang === "ka" ? 5 : 4]}</p>
          </div>
        );
      if ("data-help-encoding" in p)
        return button(() => setRaw(!raw), { "aria-pressed": raw });
      if (id === "legacy-demo")
        return <strong id={id}>{raw ? "qartuli" : "ქართული"}</strong>;
      if (id === "helpconvertinput")
        return (
          <TypingInput
            id={id}
            multiline
            value={input}
            onChange={setInput}
            aria-label="Conversion practice text"
          />
        );
      if (id === "helpconvertoutput")
        return <output id={id}>{convert(input, "_eng_", "_utf8_")}</output>;
      if (p["data-conversion-preset"])
        return button(() => setInput(p["data-conversion-preset"]));
      if (p["data-help-answer"])
        return button(() => setAnswer(p["data-help-answer"]));
      if (id === "quiz-feedback")
        return (
          <p
            id={id}
            role="status"
            className={answer === "no" ? "correct" : "retry"}
          >
            {answer &&
              (answer === "no"
                ? a.lang === "ka"
                  ? "სწორია. ჯერ გარდაქმენით ტექსტი, შემდეგ აირჩიეთ შრიფტი."
                  : "Exactly. Convert the stored characters, then choose your font."
                : a.lang === "ka"
                  ? "სცადეთ ხელახლა: შრიფტი სიმბოლოს ნახატს ცვლის."
                  : "Try again: a font changes the drawing, not the characters.")}
          </p>
        );
      if ("data-tour-step" in p)
        return button(() => setStep(+p["data-tour-step"]), {
          "aria-selected": step === +p["data-tour-step"],
        });
      if ("data-tour-prev" in p) return button(() => setStep((step + 5) % 6));
      if ("data-tour-next" in p) return button(() => setStep((step + 1) % 6));
      if (id === "tour-progress") return <span id={id}>{step + 1} / 6</span>;
      if (p.class === "tour-copy")
        return (
          <div className="tour-copy">
            {parse(tours[step]?.innerHTML || "", options)}
          </div>
        );
      if (p.class === "tour-demo")
        return (
          <div className="tour-demo">
            {step === 0 ? (
              <>
                <div className="mini-search">⌕ Fontlab</div>
                <div className="mini-font">
                  ქართული ამბავი<small>Bakuri · Unicode</small>
                </div>
                <div className="mini-font alt">
                  ქართული ამბავი<small>3D Unicode</small>
                </div>
              </>
            ) : step === 1 ? (
              <div className="mini-poster">
                <small>FONTLAB / PREVIEW STUDIO</small>
                <strong>შენი ახალი ამბავი.</strong>
              </div>
            ) : step === 2 ? (
              <div className="mini-comparison">
                <div className="mini-font">ქართული</div>
                <div className="mini-font alt">ქართული</div>
              </div>
            ) : step === 3 ? (
              <div className="mini-conversion">
                <span>gamarjoba</span>
                <b>↓</b>
                <strong>გამარჯობა</strong>
                <small>Latin → Unicode</small>
              </div>
            ) : step === 4 ? (
              <div className="mini-web">
                <span>① &lt;head&gt;</span>
                <code>
                  &lt;link rel="stylesheet" href="…/css?family=905"&gt;
                </code>
                <span>② styles.css</span>
                <code>font-family: 'Bakuri Regular';</code>
                <strong>ქართული ვებზე.</strong>
              </div>
            ) : (
              <div className="mini-download">
                <span>↓</span>
                <h3>Bakuri Regular</h3>
                <p>Original font file</p>
                <div>Details & license ↗</div>
              </div>
            )}
          </div>
        );
      if ("data-diagnose" in p)
        return button(() => setDiagnosis(+p["data-diagnose"]), {
          "aria-pressed": diagnosis === +p["data-diagnose"],
        });
      if (id === "diagnosis-title")
        return (
          <h3 id={id}>
            {diagnoses[diagnosis]?.querySelector("h3").textContent}
          </h3>
        );
      if (id === "diagnosis-answer")
        return (
          <p id={id} role="status">
            {diagnoses[diagnosis]?.querySelector("p").textContent}
          </p>
        );
      if (
        node.name === "a" &&
        p.href?.startsWith("#") &&
        !["#explore", "#converter", "#help", "#studio"].includes(p.href)
      )
        return button((e) => {
          e.preventDefault();
          document
            .querySelector(p.href)
            ?.scrollIntoView({ behavior: "smooth" });
        });
      if (node.name === "a" && p.href === "#studio")
        return button((e) => {
          e.preventDefault();
          a.open("studio", "905");
        });
    },
  };
  return <>{parse(markup, options)}</>;
}
